import React from 'react';
import { formatCurrency, numberToWords } from '../../utils/formatters';

export interface BusinessDetails {
  name: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  gstin: string;
  phone: string;
  email: string;
  bankName: string;
  accountNumber: string;
  ifsc: string;
  logoUrl?: string;
  upiId?: string;
}

interface InvoiceItem {
  id: number;
  name: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  inventoryItemId?: string;
  hsnCode?: string;
  gstRate?: number;
}

// Define AdditionalCharge interface
interface AdditionalCharge {
  id: number;
  name: string;
  amount: number;
  taxable?: boolean;
}

interface InvoiceDetails {
  id: string;
  invoiceNumber: string;
  client: string;
  clientId?: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
    state?: string;
  };
  date: string;
  dueDate: string;
  paymentDate?: string;
  items: InvoiceItem[];
  additionalCharges?: AdditionalCharge[];
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  taxable: boolean;
  discountType: string;
  discountValue: number;
  subtotal: number;
  tax: number;
  discount: number;
  additionalChargesTotal?: number;
  amount: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  createdAt: string;
  userId: string;
  paymentInfo?: {
    amountReceived: number;
    paymentMode: 'cash' | 'bank' | 'upi';
    bankAccount?: {
      bank: string;
      accountNumber: string;
    };
    paymentDate: string;
    paymentReference?: string;
  };
  bankDetails?: {
    bankName: string;
    accountNumber: string;
    ifscCode: string;
  };
}

interface IndianGSTInvoiceTemplateProps {
  invoice: InvoiceDetails;
  business: BusinessDetails;
  showWatermark?: boolean;
  primaryColor?: string;
}

const IndianGSTInvoiceTemplate: React.FC<IndianGSTInvoiceTemplateProps> = ({
  invoice,
  business,
  showWatermark = false,
  primaryColor = '#1A237E',
}) => {
  // Determine if it's interstate supply based on GSTN numbers
  const isInterStateSupply = () => {
    if (!business.gstin || !invoice.clientDetails.gstin) return false;
    
    // Extract first two digits from both GSTNs
    const sellerStateCode = business.gstin.substring(0, 2);
    const buyerStateCode = invoice.clientDetails.gstin.substring(0, 2);
    
    // If state codes are different, it's interstate supply
    return sellerStateCode !== buyerStateCode;
  };

  const isInterState = isInterStateSupply();

  // Calculate tax details
  const calculateTaxDetails = () => {
    let totalTaxableValue = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;
    
    invoice.items.forEach(item => {
      const taxableValue = item.quantity * item.rate;
      totalTaxableValue += taxableValue;
      
      if (item.gstRate) {
        const taxRate = item.gstRate;
        if (isInterState) {
          // IGST for interstate
          totalIGST += (taxableValue * taxRate / 100);
        } else {
          // CGST + SGST for intrastate
          totalCGST += (taxableValue * taxRate / 200); // Half of GST rate
          totalSGST += (taxableValue * taxRate / 200); // Half of GST rate
        }
      }
    });
    
    return { totalTaxableValue, totalCGST, totalSGST, totalIGST };
  };

  const { totalTaxableValue, totalCGST, totalSGST, totalIGST } = calculateTaxDetails();

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="invoice-container" style={{ fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto', border: '1px solid #ddd', padding: '20px', position: 'relative' }}>
      {/* Watermark */}
      {showWatermark && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-45deg)',
          fontSize: '72px',
          color: 'rgba(200, 200, 200, 0.2)',
          pointerEvents: 'none',
          zIndex: 0,
          width: '100%',
          textAlign: 'center'
        }}>
          {invoice.status === 'paid' ? 'PAID' : invoice.status.toUpperCase()}
        </div>
      )}

      {/* Header Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: primaryColor, marginBottom: '5px' }}>{business.name}</h2>
          <p>{business.address}</p>
          <p>{business.city}, {business.state} - {business.pincode}</p>
          <p>GSTN: {business.gstin}</p>
          <p>Phone: {business.phone}</p>
          <p>Email: {business.email}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          {business.logoUrl && (
            <img 
              src={business.logoUrl} 
              alt={`${business.name} Logo`} 
              style={{ maxHeight: '80px', maxWidth: '200px', marginBottom: '10px' }}
            />
          )}
        </div>
      </div>

      {/* Invoice Title Bar */}
      <div style={{ 
        backgroundColor: primaryColor, 
        color: 'white', 
        padding: '10px', 
        display: 'flex', 
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <div>TAX INVOICE</div>
        <div>ORIGINAL FOR RECIPIENT</div>
        <div>{isInterState ? 'INTERSTATE SUPPLY' : 'INTRASTATE SUPPLY'}</div>
      </div>

      {/* Invoice Details */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        backgroundColor: '#f5f5f5',
        padding: '10px',
        marginBottom: '20px'
      }}>
        <div>
          <p><strong>Invoice No:</strong> {invoice.invoiceNumber}</p>
          <p><strong>Invoice Date:</strong> {formatDate(invoice.date)}</p>
          <p><strong>Due Date:</strong> {formatDate(invoice.dueDate)}</p>
        </div>
        <div>
          <p><strong>Place of Supply:</strong> {invoice.clientDetails.state || 'Not Specified'}</p>
          <p><strong>Reverse Charge:</strong> No</p>
        </div>
      </div>

      {/* Client Info */}
      <div style={{ border: '1px solid #ddd', padding: '15px', marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0, color: primaryColor }}>BILL TO</h3>
        <p><strong>{invoice.client}</strong></p>
        <p>{invoice.clientDetails.address}</p>
        <p>GSTN: {invoice.clientDetails.gstin}</p>
        {invoice.clientDetails.phone && <p>Phone: {invoice.clientDetails.phone}</p>}
        {invoice.clientDetails.email && <p>Email: {invoice.clientDetails.email}</p>}
      </div>

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr style={{ backgroundColor: '#f2f2f2' }}>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>#</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Item & Description</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>HSN/SAC</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Qty</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Rate</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>GST %</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items.map((item, index) => {
            const taxableValue = item.quantity * item.rate;
            return (
              <tr key={item.id}>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>{index + 1}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                  <div><strong>{item.name}</strong></div>
                  {item.description && <div style={{ fontSize: '0.9em' }}>{item.description}</div>}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{item.hsnCode || 'N/A'}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{item.quantity}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatCurrency(item.rate)}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{item.gstRate ? `${item.gstRate}%` : 'N/A'}</td>
                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatCurrency(taxableValue)}</td>
              </tr>
            );
          })}
          
          {/* Additional Charges */}
          {invoice.additionalCharges && invoice.additionalCharges.length > 0 && (
            <>
              <tr>
                <td colSpan={7} style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#f9f9f9' }}>
                  <strong>Additional Charges</strong>
                </td>
              </tr>
              {invoice.additionalCharges.map((charge, index) => (
                <tr key={`charge-${charge.id}`}>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}></td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                    <div><strong>{charge.name}</strong></div>
                  </td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>-</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>-</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>-</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{formatCurrency(charge.amount)}</td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>

      {/* Summary and Totals */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ width: '45%' }}>
          <h3 style={{ color: primaryColor, marginTop: 0 }}>Amount in Words:</h3>
          <p style={{ fontStyle: 'italic' }}>{numberToWords(invoice.amount)} Rupees Only</p>
          
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ color: primaryColor, marginBottom: '10px' }}>BANK DETAILS:</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '5px', border: '1px solid #ddd' }}>Bank Name</td>
                  <td style={{ padding: '5px', border: '1px solid #ddd' }}>
                    {invoice.bankDetails?.bankName || business.bankName || 'N/A'}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '5px', border: '1px solid #ddd' }}>Account Number</td>
                  <td style={{ padding: '5px', border: '1px solid #ddd' }}>
                    {invoice.bankDetails?.accountNumber || business.accountNumber || 'N/A'}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '5px', border: '1px solid #ddd' }}>IFSC Code</td>
                  <td style={{ padding: '5px', border: '1px solid #ddd' }}>
                    {invoice.bankDetails?.ifscCode || business.ifsc || 'N/A'}
                  </td>
                </tr>
                {business.upiId && (
                  <tr>
                    <td style={{ padding: '5px', border: '1px solid #ddd' }}>UPI ID</td>
                    <td style={{ padding: '5px', border: '1px solid #ddd' }}>{business.upiId}</td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {business.upiId && (
              <div style={{ marginTop: '15px', textAlign: 'center', border: '1px solid #ddd', padding: '10px', borderRadius: '4px', display: 'inline-block' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: primaryColor }}>Scan to Pay</div>
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`upi://pay?pa=${business.upiId}&pn=${encodeURIComponent(business.name)}&am=${invoice.amount}&cu=INR`)}`} 
                  alt="UPI QR Code" 
                  style={{ width: '80px', height: '80px' }} 
                />
              </div>
            )}
          </div>
        </div>
        
        <div style={{ width: '45%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: '5px', border: '1px solid #ddd' }}>Subtotal</td>
                <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right' }}>{formatCurrency(totalTaxableValue)}</td>
              </tr>
              {invoice.discount > 0 && (
                <tr>
                  <td style={{ padding: '5px', border: '1px solid #ddd' }}>Discount</td>
                  <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right' }}>-{formatCurrency(invoice.discount)}</td>
                </tr>
              )}
              {isInterState ? (
                <tr>
                  <td style={{ padding: '5px', border: '1px solid #ddd' }}>IGST</td>
                  <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right' }}>{formatCurrency(totalIGST)}</td>
                </tr>
              ) : (
                <>
                  <tr>
                    <td style={{ padding: '5px', border: '1px solid #ddd' }}>CGST</td>
                    <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right' }}>{formatCurrency(totalCGST)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '5px', border: '1px solid #ddd' }}>SGST</td>
                    <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right' }}>{formatCurrency(totalSGST)}</td>
                  </tr>
                </>
              )}
              
              {/* Display Additional Charges Total */}
              {invoice.additionalChargesTotal && invoice.additionalChargesTotal > 0 && (
                <tr>
                  <td style={{ padding: '5px', border: '1px solid #ddd' }}>Additional Charges</td>
                  <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {formatCurrency(invoice.additionalChargesTotal)}
                  </td>
                </tr>
              )}
              
              <tr style={{ backgroundColor: '#f2f2f2' }}>
                <td style={{ padding: '5px', border: '1px solid #ddd' }}><strong>TOTAL</strong></td>
                <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(invoice.amount)}</td>
              </tr>
            </tbody>
          </table>
          
          {invoice.paymentInfo && (
            <div style={{ marginTop: '20px', border: '1px solid #ddd', padding: '10px' }}>
              <h3 style={{ color: primaryColor, marginTop: 0, marginBottom: '10px' }}>Payment Information</h3>
              <p><strong>Amount Received:</strong> {formatCurrency(invoice.paymentInfo.amountReceived)}</p>
              <p><strong>Payment Mode:</strong> {invoice.paymentInfo.paymentMode.toUpperCase()}</p>
              <p><strong>Payment Date:</strong> {formatDate(invoice.paymentInfo.paymentDate)}</p>
              {invoice.paymentInfo.paymentReference && (
                <p><strong>Reference:</strong> {invoice.paymentInfo.paymentReference}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Notes Section */}
      <div style={{ marginBottom: '20px' }}>
        {invoice.notes && (
          <>
            <h3 style={{ color: primaryColor }}>Notes:</h3>
            <p>{invoice.notes}</p>
          </>
        )}
      </div>

      {/* Terms and Conditions */}
      <div style={{ marginBottom: '20px', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
        <h3 style={{ color: primaryColor }}>Terms and Conditions:</h3>
        <p style={{ whiteSpace: 'pre-line' }}>{invoice.termsAndConditions}</p>
      </div>

      {/* Signatures Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px' }}>
        <div style={{ width: '45%' }}>
          <p><strong>For {invoice.client}</strong></p>
          <div style={{ marginTop: '50px' }}>
            <p>Authorized Signatory</p>
          </div>
        </div>
        <div style={{ width: '45%', textAlign: 'right' }}>
          <p><strong>For {business.name}</strong></p>
          <div style={{ marginTop: '50px' }}>
            <p>Authorized Signatory</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: '30px', textAlign: 'center', borderTop: '1px solid #ddd', paddingTop: '15px', fontSize: '12px', color: '#777' }}>
        <p>This is a computer-generated invoice and doesn't require a physical signature.</p>
        <p>Generated using Accounts by AkMo Filings</p>
      </div>
    </div>
  );
};

export default IndianGSTInvoiceTemplate; 